import type { BanDich } from '../../types';
import type { viRpaLoi } from './loi.vi';

export const enRpaLoi: Pick<BanDich, keyof typeof viRpaLoi> = {
  rpaLoi: {
    gioiThieu:
      'The error table starts empty. When an OpenRPA flow catches an exception, the robot reports it here; you create an error type from that exception and set how to handle it. Exceptions without handling are tried once, skipped and reported, then the run continues.',
    mucDo: {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    },
    cho: {
      tieuDe: 'Exceptions waiting for handling',
      moTa: 'Robots reported these exceptions but they do not belong to any error type yet.',
      soDangCho: '{{so}} waiting',
      locTrangThai: 'Filter by status',
      PENDING: 'Waiting',
      IGNORED: 'Ignored',
      rong: 'No exceptions waiting',
      rongMoTa: 'Run a flow that fails; exceptions the robot reports will appear here for you to handle.',
      rongBoQua: 'No ignored exceptions',
      soLan: 'Seen {{so}} times',
      lanCuoi: 'Last seen {{luc}}',
      buoc: 'Step: {{buoc}}',
      luong: 'Workflow: {{luong}}',
      taoCode: 'Create error type',
      gan: 'Assign to an existing error type',
      chuaCoCode: 'There is no error type to assign to yet',
      boQua: 'Ignore',
      dangBoQua: 'Ignoring...',
      daBoQua: 'Ignored {{exception}}.',
      danhSach: 'Waiting exceptions',
    },
    code: {
      tieuDe: 'Error types',
      moTa: 'Each row is an error type and how the robot handles it.',
      them: 'Add error type',
      rong: 'No error types yet',
      rongMoTa: 'Create one from a waiting exception above, or click Add error type.',
      cotMa: 'Code',
      cotTen: 'Name',
      cotTinhChat: 'Nature',
      cotMucDo: 'Severity',
      cotCachXuLy: 'Handling',
      cotException: 'Exceptions',
      cotBat: 'On',
      luongTruoc: 'Foreseen',
      batNgo: 'Unexpected',
      thuLai: 'Retry {{so}} times, {{giay}} seconds apart',
      khongThuLai: 'No retry',
      hetLanSKIP: 'When retries run out, skip and report',
      hetLanSTOP: 'When retries run out, stop the run',
      xemException: 'View exceptions',
      dangXem: 'Viewing',
      sua: 'Edit error type',
      xoa: 'Delete error type',
      xoaBiChan: 'Remove all exceptions of this error type before deleting it.',
      xoaTieuDe: 'Delete error type {{ma}}?',
      xoaThan: 'Error type {{ma}} ({{ten}}) will be deleted. This cannot be undone.',
      daXoa: 'Deleted error type {{ma}}.',
      batNhan: 'Turn on error type {{ma}}',
      daBat: 'Turned on error type {{ma}}.',
      daTat: 'Turned off error type {{ma}}.',
    },
    chiTiet: {
      tieuDe: 'Exceptions of the error type',
      tieuDeCua: 'Exceptions of error type {{ma}} · {{ten}}',
      moTa: 'Exceptions (and messages, if needed) counted as this error type.',
      them: 'Add exception',
      rong: 'This error type has no exceptions yet',
      rongMoTa: 'Add an exception so robots recognise this error type.',
      chuaCoCode: 'There is no error type to view yet',
      cotException: 'Exception',
      cotThongDiep: 'Message contains',
      cotPhanLoai: 'Classification',
      cotSoLan: 'Times seen',
      cotLanCuoi: 'Last seen',
      cotBat: 'On',
      moiThongDiep: 'Any message',
      chuaPhanLoai: 'Unclassified',
      sua: 'Edit exception',
      go: 'Remove exception',
      goTieuDe: 'Remove {{exception}} from the error type?',
      goThan:
        '{{exception}} will no longer belong to this error type. The next time a robot reports it, it goes back to the waiting list. This cannot be undone.',
      daGo: 'Removed exception {{exception}}.',
      batNhan: 'Turn on {{exception}}',
      daBat: 'Turned on exception {{exception}}.',
      daTat: 'Turned off exception {{exception}}.',
    },
    form: {
      taoTieuDe: 'Create error type',
      suaTieuDe: 'Edit error type {{ma}}',
      tuCho: 'Created from a waiting exception',
      ten: 'Error type name',
      tenGoiY: 'A name operators can read, for example Network connection lost.',
      tenMau: 'Network connection lost',
      tinhChat: 'Nature',
      tinhChatGoiY: 'Foreseen means you know it can happen.',
      luongTruoc: 'Foreseen',
      batNgo: 'Unexpected',
      mucDo: 'Severity',
      moTa: 'Description',
      moTaGoiY: 'Cause and how to handle it, in plain words.',
      cachXuLy: 'Handling',
      cachXuLyGoiY: 'Robots follow these values every time they hit this error type.',
      soLan: 'Retries',
      soLanGoiY: '0 means no retry. At most {{max}}.',
      gianCach: 'Seconds between retries',
      gianCachGoiY: 'At most {{max}} seconds.',
      giay: 'seconds',
      khiHet: 'When retries run out',
      khiHetGoiY: 'What to do when every retry still fails.',
      SKIP: 'Skip and report',
      STOP: 'Stop the run',
      bat: 'On',
      batGoiY: 'When off, exceptions of this error type are treated as having no handling.',
      loiTen: 'Enter an error type name.',
      loiSoLan: 'Retries must be a whole number from 0 to 10.',
      loiGianCach: 'Seconds must be a whole number from 0 to 3600.',
      tao: 'Create error type',
      luu: 'Save error type',
      daTao: 'Created error type {{ma}}.',
      daLuu: 'Saved error type {{ma}}.',
    },
    formCt: {
      themTieuDe: 'Add exception to error type {{ma}}',
      suaTieuDe: 'Edit exception',
      exception: 'Exception',
      exceptionGoiY: 'Full name, for example System.Net.WebException.',
      exceptionMau: 'System.NullReferenceException',
      thongDiep: 'Message contains',
      thongDiepGoiY: 'Leave empty if this error type applies to every message of the exception. Not case-sensitive.',
      phanLoai: 'Classification',
      phanLoaiGoiY: 'Pick a classification already in use or type a new one.',
      phanLoaiChon: 'Pick or type a classification',
      phanLoaiRong: 'No classifications yet, type to create one',
      phanLoaiThem: 'Add classification "{{ten}}"',
      bat: 'On',
      batGoiY: 'When off, this row is not used to recognise the error type.',
      loiBatBuoc: 'Enter an exception name.',
      loiSaiDang: 'An exception name only has letters, digits, dots and underscores, for example System.NullReferenceException.',
      them: 'Add exception',
      luu: 'Save exception',
      daThem: 'Added exception {{exception}}.',
      daLuu: 'Saved exception {{exception}}.',
    },
    gan: {
      tieuDe: 'Assign exception to an error type',
      code: 'Error type',
      codeGoiY: 'The exception will belong to this error type and match every message.',
      codeChon: 'Pick an error type',
      loiChon: 'Pick an error type.',
      gan: 'Assign to error type',
      daGan: 'Assigned to error type {{ma}}.',
    },
  },
};
